import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { Event } from '@patsnap/tahiti-core/common/event';
import { SpecificationConfirmClaimsRepository } from '../specification-confirm-claims-repository';
import { SpecificationConfirmClaimsModel } from '../../models/specification-confirm-claims-model';
import type { SpecificationConfirmClaimsService } from '../specification-confirm-claims-service';
import type {
    SpecificationConfirmClaimsData,
    SpecificationConfirmClaimsState,
    SpecificationConfirmClaimTree,
} from '../../types/specification-confirm-claims';
import { Disposable } from '@patsnap/tahiti-core';
import type { SpecificationStreamContent } from '../../types/specification';
import { SpecificationService } from '../specification-service';
import { SpecificationStep } from '../../enums';
import { CheckSensitiveWordService } from '@/services/check-sensitive-word/check-sensitive-word';
import { treeEach } from '@telecom/shared/utils/tree';
import { Specification } from './specification';

@injectable()
export class SpecificationConfirmClaimsServiceImpl implements SpecificationConfirmClaimsService {
    private _disposables: Disposable[] = [];

    // eslint-disable-next-line max-params
    constructor(
        @inject(SpecificationConfirmClaimsRepository)
        private _repository: SpecificationConfirmClaimsRepository,
        @inject(SpecificationConfirmClaimsModel)
        private _model: SpecificationConfirmClaimsModel,
        @inject(SpecificationService)
        private _specificationService: SpecificationService,
        @inject(CheckSensitiveWordService)
        private _checkSensitiveWordService: CheckSensitiveWordService,
        @inject(Specification)
        private _specification: Specification,
    ) {}

    get onStateChange(): Event<SpecificationConfirmClaimsState> {
        return this._model.onStatesChange;
    }

    private get _currentTaskId(): string | null {
        return this._model.modelData.taskId;
    }

    async init(taskId: string, regenerate?: boolean): Promise<void> {
        if (this._currentTaskId === taskId && !regenerate) {
            return;
        }

        this._model.update(state => ({
            ...state,
            taskId,
            loading: true,
        }));

        await this._initializeFormData(taskId, regenerate);
        this._model.update(state => ({
            ...state,
            loading: false,
        }));
    }

    private async _initializeFormData(taskId: string, regenerate?: boolean): Promise<any[]> {
        this._disposeAll();

        const providers = this._getProviders(taskId, regenerate);
        const deferrers = await this._subscribeToDeferrers(providers);

        return Promise.allSettled(deferrers.map(deferred => deferred.promise));
    }

    private _getProviders(taskId: string, regenerate?: boolean) {
        return {
            formattedClaim: this._repository.genFormattedClaim(taskId),
            claimTree: this._repository.genClaimTree(taskId, regenerate),
        };
    }

    private async _subscribeToDeferrers(providers: ReturnType<typeof this._getProviders>) {
        const deferrers = [
            this._subscribeToFormattedClaim(providers.formattedClaim),
            this._subscribeToClaimTree(providers.claimTree),
        ];

        return deferrers;
    }

    private _subscribeToFormattedClaim(provider: ReturnType<typeof this._repository.genFormattedClaim>) {
        const deferred = provider.subscribe((formattedClaim: SpecificationStreamContent<string>) => {
            this._model.update(state => ({
                ...state,
                formData: {
                    ...state.formData,
                    formattedClaim,
                },
            }));
        });
        this._disposables.push(Disposable.create(provider.abort));
        return deferred;
    }

    private _subscribeToClaimTree(provider: ReturnType<typeof this._repository.genClaimTree>) {
        const deferred = provider.subscribe((claimTree) => {
            this._model.update(state => ({
                ...state,
                formData: {
                    ...state.formData,
                    claimTree,
                },
            }));
        });
        this._disposables.push(Disposable.create(provider.abort));
        return deferred;
    }

    private _disposeAll(): void {
        this._disposables.forEach(disposable => disposable.dispose());
        this._disposables = [];
    }

    getState(): SpecificationConfirmClaimsState {
        return this._model.modelData;
    }

    patchFormData(payload: Partial<SpecificationConfirmClaimsData>): void {
        this._model.update(state => ({
            ...state,
            formData: {
                ...state.formData,
                ...payload,
            },
        }));
    }

    async previous(): Promise<void> {
        this._specificationService.switchStep(SpecificationStep.ConfirmDisclosure);
    }

    async submit(switchStep = true): Promise<void> {
        const { taskId, formData } = this.getState();
        this._model.update(state => ({
            ...state,
            loadingSubmit: true,
        }));

        try {
            await this._checkSensitiveWordService.checkSensitiveWord(this._getClaimTreeContent(formData.claimTree.content));
            await this._repository.submit(taskId, {
                claimTree: formData.claimTree.content!,
                specification: this._specification,
            });
            if (switchStep) {
                this._specificationService.switchStep(SpecificationStep.GenContent);
            }
        } finally {
            this._model.update(state => ({
                ...state,
                loadingSubmit: false,
            }));
        }
    }

    async regenerate(): Promise<void> {
        const { taskId } = this.getState();
        this._disposeAll();
        // 等待取消结束
        await new Promise(resolve => setTimeout(resolve, 0));
        await this.init(taskId, true);
    }

    private _getClaimTreeContent(claimTree: SpecificationConfirmClaimTree): string {
        let content = '';
        treeEach(claimTree, (node) => {
            content += `${node.simpleClaim}\n`;
            if (node.claimFeatures.length) {
                content += `${node.claimFeatures.map(feature => feature.feature).join('\n')}\n`;
            }
        });
        return content;
    }
}
